import { Injectable } from '@angular/core';
import { CgeNews, NewsItem } from '@qvs/core';
import { combineLatest, firstValueFrom, from, Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { FileOpener } from '@capacitor-community/file-opener';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { selectIsAuthenticated } from '@qvs/core/auth';
import { selectEnvironment } from '@qvs/core/env';
import { selectUserProfile } from '@qvs/core/user';
import * as wizeMobileApi from '@wize-mobile-api';
import * as newsApi from '@news-api';
import { NewsMapper } from '~/modules/news/news.mapper';
import { CACHE_FOLDERS, FileCacheService } from '~/shared/services/file-cache.service';
import { FileBlobOpenerService } from '~/shared/services/file-blob-opener.service';
import { environment } from '#environment';
import { loadNewsFailure, loadNewsSuccess, markNewsAsRead } from './slice';
import { selectLang } from '@qvs/core/preference';

export const PAGE_SIZE_FOR_CGE_NEWS = 20;

@Injectable({
  providedIn: 'root'
})
export class NewsService {
  isCgeNewsEnabled = environment.featureFlags.isCgeNewsEnabled;
  private readonly cacheDir = CACHE_FOLDERS.NEWS;

  constructor(
    private readonly store: Store,
    private readonly fileCacheService: FileCacheService,
    private readonly dataFeedApi: wizeMobileApi.DatafeedMobileService,
    private readonly cgeNewsService: newsApi.NewsService,
    private readonly fileBlobOpenerService: FileBlobOpenerService
  ) {}

  init() {
    /**
     * load news
     */
    combineLatest([
      this.store.pipe(select(selectIsAuthenticated)),
      this.store.pipe(select(selectEnvironment)),
      this.store.pipe(select(selectUserProfile))
    ])
      .pipe(
        switchMap(([authenticated, env, userProfile]) => {
          if (authenticated && env && userProfile && !this.isCgeNewsEnabled) {
            return this.loadNews().pipe(
              catchError((e) => {
                this.store.dispatch(loadNewsFailure());
                return of(e);
              })
            );
          }
          return of(null);
        }),
        filter((items) => !!items)
      )
      .subscribe((items) => {
        this.store.dispatch(loadNewsSuccess({ items }));
      });

    return Promise.resolve();
  }

  loadNews(): Observable<NewsItem[]> {
    return this.dataFeedApi.listNews().pipe(switchMap((items) => of(NewsMapper.fromWizeNewsItems(items))));
  }

  loadCgeNews(page: number, pageSize: number): Observable<CgeNews> {
    return this.store.pipe(
      select(selectLang),
      switchMap((language) =>
        this.cgeNewsService
          .listNews(page, pageSize, null, language.code)
          .pipe(switchMap((newsResponse) => of(NewsMapper.fromCgeNewsItems(newsResponse))))
      )
    );
  }

  markNewsAsRead(newsId: number) {
    this.store.dispatch(markNewsAsRead({ newsId }));
  }

  loadPdfBase64Encoded(newItem: NewsItem): Observable<string> {
    if (!newItem.pdfMediaId) {
      return of(null);
    }
    return this.dataFeedApi.getMediaDetails(newItem.id, newItem.pdfMediaId).pipe(switchMap((media) => of(media.base64Content)));
  }

  async openPdf(newsItem: NewsItem): Promise<void> {
    const fileName = `${newsItem.title}.pdf`;
    if (!(await this.fileCacheService.isFileInCache(fileName, this.cacheDir))) {
      await firstValueFrom(this.downloadPdf(newsItem, fileName));
    }
    return FileOpener.open({
      filePath: `${await this.fileCacheService.getCacheDirectory(this.cacheDir)}/${fileName}`,
      contentType: 'application/pdf'
    });
  }

  async openPdfInBrowser(newsItem: NewsItem): Promise<void> {
    const base64Content = await firstValueFrom(this.loadPdfBase64Encoded(newsItem));
    if (!base64Content) {
      return;
    }

    this.fileBlobOpenerService.openInBrowser(base64Content, 'application/pdf');
  }

  private downloadPdf(newsItem: NewsItem, fileName: string): Observable<string> {
    return this.loadPdfBase64Encoded(newsItem).pipe(
      switchMap((base64encoded) => {
        if (!base64encoded) {
          return;
        }

        return from(this.fileCacheService.writeFile(fileName, this.cacheDir, base64encoded).then((file) => file.uri));
      })
    );
  }
}
